import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Bath, BedDouble, Bookmark, Car, ChevronRight, ExternalLink, Heart, LandPlot, X } from 'lucide-react'
import { doc, getDoc } from 'firebase/firestore'
import { Button } from '@/components/ui/button'
import { FadeModal } from '@/components/ui/fade-modal'
import { Spinner } from '@/components/ui/spinner'
import { ClaimPropertyModal } from '@/components/claim/ClaimPropertyModal'
import { SaveToCollectionSheet } from '@/components/collections/SaveToCollectionSheet'
import { CreateBiddleWizard } from '@/components/property/CreateBiddleWizard'
import { StreetViewPanel } from '@/components/property/StreetViewPanel'
import { useAuth } from '@/hooks/use-auth'
import { splitAddressDisplay } from '@/lib/address'
import { getBiddleDraft, takeStashedDraft } from '@/lib/biddle-drafts'
import { openBiddleDetail } from '@/lib/biddle-navigation'
import { isPropertyInFavourites, toggleFavourite } from '@/lib/collections'
import { domainListingUrl, realestateListingUrl } from '@/lib/listing-links'
import {
  createPrefillDraftFromSearchParams,
} from '@/lib/create-biddle-prefill'
import { closePropertyProfile } from '@/lib/property-navigation'
import { openSignIn } from '@/lib/signin-navigation'
import { db } from '@/lib/firebase'
import { cn } from '@/lib/utils'
import { parseProperty, type BiddleDraft, type Property } from '@/types/models'
import biddleLogoWhite from '@/assets/biddle-logo-white.png'

function formatLandSize(landSize: number): string {
  if (!(landSize > 0)) return 'N/A'
  return `${Math.round(landSize)} m²`
}

function formatStat(value: number | null): string {
  return value == null ? '—' : String(value)
}

type ModalView = 'profile' | 'create'

/**
 * Property profile overlay — opened via `?property={id}` on any shell route.
 * Create Biddle runs in-modal (`&create=1` or the Create CTA).
 */
export function PropertyProfileModal() {
  const [searchParams, setSearchParams] = useSearchParams()
  const propertyId = searchParams.get('property')
  const startCreate = searchParams.get('create') === '1'
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isGuest, isLoading: authLoading } = useAuth()

  const [property, setProperty] = useState<Property | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [existingBiddleId, setExistingBiddleId] = useState<string | null>(null)
  const [claimOpen, setClaimOpen] = useState(false)
  const [showProcess, setShowProcess] = useState(false)
  const [view, setView] = useState<ModalView>('profile')
  const [exiting, setExiting] = useState(false)
  const [createDraft, setCreateDraft] = useState<BiddleDraft | null>(null)
  const [favourited, setFavourited] = useState(false)
  const [favBusy, setFavBusy] = useState(false)
  const [saveSheetOpen, setSaveSheetOpen] = useState(false)

  useEffect(() => {
    if (!propertyId) {
      setProperty(null)
      setError(null)
      setExistingBiddleId(null)
      setShowProcess(false)
      setView('profile')
      setExiting(false)
      setCreateDraft(null)
      return
    }
    setExiting(false)
    setView(startCreate ? 'create' : 'profile')
    if (!startCreate) setCreateDraft(null)
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const snap = await getDoc(doc(db, 'properties', propertyId))
        if (cancelled) return
        if (!snap.exists()) {
          setProperty(null)
          setError('Property not found')
        } else {
          setProperty(parseProperty(snap.id, snap.data() as Record<string, unknown>))
        }
      } catch (e) {
        if (!cancelled) {
          setProperty(null)
          setError(e instanceof Error ? e.message : 'Failed to load property')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [propertyId, startCreate])

  const prefillKey = startCreate
    ? [
        searchParams.get('price') ?? '',
        searchParams.get('deposit') ?? '',
        searchParams.get('settlement') ?? '',
        searchParams.get('notes') ?? '',
      ].join('\0')
    : ''

  useEffect(() => {
    if (!propertyId || !startCreate) {
      setCreateDraft(null)
      return
    }
    const stashed = takeStashedDraft(propertyId)
    if (stashed) {
      setCreateDraft(stashed)
      return
    }
    let cancelled = false
    ;(async () => {
      // Saved drafts win over URL prefill (Claude / deep-link handoff).
      if (user && !isGuest) {
        try {
          const draft = await getBiddleDraft(user.uid, propertyId)
          if (cancelled) return
          if (draft) {
            setCreateDraft(draft)
            return
          }
        } catch {
          // Fall through to URL prefill
        }
      }
      if (cancelled) return
      const fullAddress = property?.fullAddress ?? ''
      setCreateDraft(
        createPrefillDraftFromSearchParams(propertyId, fullAddress, searchParams),
      )
    })()
    return () => {
      cancelled = true
    }
    // prefillKey captures the deep-link term params without depending on the
    // URLSearchParams object identity.
  }, [propertyId, startCreate, user, isGuest, property?.fullAddress, prefillKey])

  useEffect(() => {
    if (!propertyId || !user || isGuest) {
      setExistingBiddleId(null)
      setFavourited(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const marker = await getDoc(doc(db, 'users', user.uid, 'biddlesByProperty', propertyId))
        if (cancelled) return
        if (!marker.exists()) {
          setExistingBiddleId(null)
        } else {
          const data = marker.data() as Record<string, unknown>
          const biddleId =
            (typeof data.biddleId === 'string' && data.biddleId) ||
            (typeof data.biddleID === 'string' && data.biddleID) ||
            null
          setExistingBiddleId(biddleId)
        }
      } catch {
        if (!cancelled) setExistingBiddleId(null)
      }
      try {
        const fav = await isPropertyInFavourites(user.uid, propertyId)
        if (!cancelled) setFavourited(fav)
      } catch {
        if (!cancelled) setFavourited(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [propertyId, user, isGuest])

  /** On Map, create sits on top of the property profile — Back/X returns there.
   *  Elsewhere (e.g. Drafts on Biddles), create was opened directly — dismiss fully. */
  const returnCreateToProfile = location.pathname === '/map'

  const dismissModal = useCallback(() => {
    if (exiting) return
    setExiting(true)
    window.setTimeout(() => {
      closePropertyProfile(navigate)
    }, 260)
  }, [exiting, navigate])

  const exitCreate = useCallback(() => {
    if (returnCreateToProfile) {
      setView('profile')
      const next = new URLSearchParams(searchParams)
      next.delete('create')
      for (const key of ['price', 'deposit', 'settlement', 'notes'] as const) {
        next.delete(key)
      }
      setSearchParams(next, { replace: true })
      return
    }
    dismissModal()
  }, [returnCreateToProfile, searchParams, setSearchParams, dismissModal])

  const onClose = useCallback(() => {
    if (exiting) return
    if (view === 'create') {
      exitCreate()
      return
    }
    dismissModal()
  }, [exiting, view, exitCreate, dismissModal])

  useEffect(() => {
    if (!propertyId) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Process info modal handles its own Escape via FadeModal.
      if (showProcess) return
      if (view === 'create') exitCreate()
      else onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [propertyId, view, exitCreate, onClose, showProcess])

  if (!propertyId) return null

  const signedIn = Boolean(user) && !isGuest
  const returnToCreate = `${location.pathname}?property=${encodeURIComponent(propertyId)}&create=1`
  const { street, locality } = property
    ? splitAddressDisplay(property.fullAddress)
    : { street: '', locality: '' }

  function enterCreate() {
    setView('create')
    const next = new URLSearchParams(searchParams)
    next.set('create', '1')
    setSearchParams(next, { replace: true })
  }

  function onCreate() {
    if (!signedIn) {
      openSignIn(navigate, location, returnToCreate)
      return
    }
    enterCreate()
  }

  function onOwnProperty() {
    if (!signedIn) {
      openSignIn(navigate, location, `/claim-property?propertyId=${propertyId}`)
      return
    }
    setClaimOpen(true)
  }

  function onCreated(biddleId: string) {
    if (!propertyId) return
    openBiddleDetail(navigate, propertyId, biddleId, { replace: true })
  }

  function requireSignInForSave() {
    if (!propertyId) return
    openSignIn(
      navigate,
      location,
      `${location.pathname}?property=${encodeURIComponent(propertyId)}`,
    )
  }

  async function onHeartClick() {
    if (!signedIn || !user || !property) {
      requireSignInForSave()
      return
    }
    setFavBusy(true)
    try {
      const { favourited: next } = await toggleFavourite(user.uid, property.id, property.fullAddress)
      setFavourited(next)
    } catch {
      // keep prior state
    } finally {
      setFavBusy(false)
    }
  }

  function onPlusClick() {
    if (!signedIn || !user) {
      requireSignInForSave()
      return
    }
    setSaveSheetOpen(true)
  }

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-50 flex items-end justify-center bg-navy/60 p-0 backdrop-blur-sm sm:items-center sm:p-4',
          exiting ? 'map-sheet-backdrop-exit' : 'map-sheet-backdrop-enter',
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="property-profile-title"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose()
        }}
      >
        <div
          className={cn(
            'relative flex max-h-[min(92dvh,800px)] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl',
            exiting ? 'property-profile-panel-exit' : 'property-profile-panel-enter',
          )}
        >
          {/* Address + actions stay fixed at the top while the body scrolls underneath. */}
          {view === 'profile' && !loading && !authLoading && property ? (
            <div className="relative z-20 shrink-0 border-b border-border/50 bg-white px-5 pb-3.5 pt-7">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <h2
                    id="property-profile-title"
                    className="text-2xl font-bold tracking-tight text-navy"
                  >
                    {street || 'Address unavailable'}
                  </h2>
                  {locality ? (
                    <p className="text-[15px] font-semibold text-muted">{locality}</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-0.5 pt-0.5">
                  <button
                    type="button"
                    disabled={favBusy}
                    onClick={() => void onHeartClick()}
                    className="rounded-lg p-2 text-muted transition hover:bg-surface hover:text-charcoal disabled:opacity-50"
                    aria-label={favourited ? 'Remove from Favourites' : 'Add to Favourites'}
                  >
                    <Heart
                      className={cn(
                        'h-5 w-5',
                        favourited && 'fill-biddle-blue text-biddle-blue',
                      )}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={onPlusClick}
                    className="rounded-lg p-2 text-muted transition hover:bg-surface hover:text-charcoal"
                    aria-label="Add to a collection"
                  >
                    <Bookmark className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg p-2 text-muted transition hover:bg-surface hover:text-charcoal"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="absolute right-3 top-3 z-10 flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => {
                  if (view === 'create') exitCreate()
                  else onClose()
                }}
                className="rounded-lg p-2 text-muted transition hover:bg-surface hover:text-charcoal"
                aria-label={
                  view === 'create' && returnCreateToProfile ? 'Back to property' : 'Close'
                }
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}

          <div
            className={cn(
              'min-h-0 flex-1 overflow-y-auto',
              view === 'create'
                ? 'px-6 pb-7 pt-6 sm:px-8 sm:pb-8 sm:pt-7'
                : 'px-5 pb-4 pt-4',
            )}
          >
            {loading || authLoading ? (
              <div className="flex min-h-[40vh] items-center justify-center">
                <Spinner />
              </div>
            ) : error || !property ? (
              <div className="space-y-3 py-10 text-center">
                <p className="font-semibold text-navy">Property unavailable</p>
                <p className="text-sm text-muted">{error ?? 'We couldn’t find that property.'}</p>
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
              </div>
            ) : view === 'create' ? (
              !signedIn ? (
                <div className="flex flex-col gap-4 py-6">
                  <div>
                    <p className="text-sm font-medium text-biddle-blue">New Biddle</p>
                    <h2 className="mt-1 text-xl font-bold text-navy">Sign in to continue</h2>
                    <p className="mt-1 text-sm text-muted">
                      You need an account to create a Biddle on this property.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => openSignIn(navigate, location, returnToCreate)}
                    >
                      Sign in
                    </Button>
                    <Button type="button" variant="ghost" onClick={exitCreate}>
                      Back
                    </Button>
                  </div>
                </div>
              ) : (
                <CreateBiddleWizard
                  property={property}
                  onCancel={exitCreate}
                  onCreated={onCreated}
                  initialDraft={createDraft}
                />
              )
            ) : (
              <div className="flex flex-col gap-5">
                <button
                  type="button"
                  onClick={onOwnProperty}
                  className="flex w-full items-center gap-3 rounded-xl bg-biddle-blue px-4 py-3.5 text-left text-white shadow-sm transition hover:bg-biddle-blue/90"
                >
                  <span className="shrink-0 text-2xl leading-none" aria-hidden>
                    {property.hasActiveBiddles ? '🔥' : '✨'}
                  </span>
                  <span className="min-w-0 flex-1 text-sm leading-snug">
                    {property.hasActiveBiddles ? (
                      <>
                        <span className="font-bold">Hot property!</span>
                        <br />
                        This property has one or more Biddles.
                        <br />
                        <span className="font-bold">Own this property? </span>
                        <span className="font-bold underline">View history</span>
                      </>
                    ) : (
                      <>
                        <span className="font-bold">You’re the first!</span>
                        <br />
                        No active Biddles right now.
                        <br />
                        <span className="font-bold">Own this property? </span>
                        <span className="font-bold underline">View history</span>
                      </>
                    )}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-white/90" aria-hidden />
                </button>

                <div className="rounded-2xl border border-border px-4 py-4">
                  {property.propertyType ? (
                    <div className="mb-4">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted">
                        Property type
                      </p>
                      <p className="mt-1 font-semibold text-navy">{property.propertyType}</p>
                    </div>
                  ) : null}
                  <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Stat icon={LandPlot} label="Land" value={formatLandSize(property.landSize)} />
                    <Stat icon={BedDouble} label="Beds" value={formatStat(property.beds)} />
                    <Stat icon={Bath} label="Baths" value={formatStat(property.baths)} />
                    <Stat icon={Car} label="Cars" value={formatStat(property.cars)} />
                  </dl>
                </div>

                {property.location.latitude || property.location.longitude ? (
                  <StreetViewPanel
                    latitude={property.location.latitude}
                    longitude={property.location.longitude}
                  />
                ) : null}

                <ListingLinks fullAddress={property.fullAddress} />
              </div>
            )}
          </div>

          {property && !loading && view === 'profile' ? (
            <div className="shrink-0 border-t border-border bg-white/95 px-5 py-4 backdrop-blur">
              {existingBiddleId ? (
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  className="h-14 w-full text-base"
                  onClick={() => openBiddleDetail(navigate, property.id, existingBiddleId)}
                >
                  View your Biddle
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  className="h-14 w-full gap-1.5 text-[19px] font-bold"
                  onClick={onCreate}
                >
                  <span>Create a</span>
                  <img
                    src={biddleLogoWhite}
                    alt="Biddle"
                    className="h-[18px] w-auto -translate-y-[1.5px]"
                  />
                </Button>
              )}
              <button
                type="button"
                className="mt-3 flex w-full items-center justify-center gap-1.5 text-xs text-muted underline"
                onClick={() => setShowProcess(true)}
              >
                What happens after I submit my Biddle?
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <FadeModal
        open={showProcess}
        onClose={() => setShowProcess(false)}
        labelledBy="biddle-process-title"
        className="z-[80]"
      >
        <div className="relative max-h-[min(85dvh,640px)] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
          <button
            type="button"
            onClick={() => setShowProcess(false)}
            className="absolute right-3 top-3 rounded-lg p-2 text-muted transition hover:bg-surface hover:text-charcoal"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <h2 id="biddle-process-title" className="pr-10 text-lg font-bold text-navy">
            What happens after you submit your Biddle?
          </h2>
          <ol className="mt-4 list-decimal space-y-2.5 pl-5 text-sm text-muted">
            <li>
              <span className="font-medium text-navy">Notification</span> — If the owner is
              registered they’ll be notified. Higher offers are more likely to get a response.
            </li>
            <li>
              <span className="font-medium text-navy">Chat</span> — If the owner replies, you can
              message them in the Biddle chat — it appears in the Messages tab once enabled.
            </li>
            <li>
              <span className="font-medium text-navy">Negotiation</span> — Negotiate the sale terms
              directly — you can proceed with or without an agent.
            </li>
          </ol>
          <p className="mt-5 text-sm font-semibold text-navy">Important notes</p>
          <ul className="mt-1.5 list-disc space-y-1.5 pl-5 text-sm text-muted">
            <li>You must verify your identity to submit a Biddle</li>
            <li>You can withdraw your Biddle at any time</li>
            <li>Our support team is available to help with any questions</li>
          </ul>
          <Button
            type="button"
            variant="primary"
            className="mt-6 w-full"
            onClick={() => setShowProcess(false)}
          >
            Got it
          </Button>
        </div>
      </FadeModal>

      <ClaimPropertyModal
        open={claimOpen}
        onClose={() => setClaimOpen(false)}
        propertyId={propertyId}
      />

      {user && property ? (
        <SaveToCollectionSheet
          open={saveSheetOpen}
          onClose={() => setSaveSheetOpen(false)}
          uid={user.uid}
          propertyId={property.id}
          fullAddress={property.fullAddress}
          favourited={favourited}
          onFavouritedChange={setFavourited}
        />
      ) : null}
    </>
  )
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof BedDouble
  label: string
  value: string
}) {
  return (
    <div className="rounded-xl bg-surface px-3 py-3">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold text-navy">{value}</div>
    </div>
  )
}

/** Listing site links — same URLs as iOS, styled to match web outline buttons. */
function ListingLinks({ fullAddress }: { fullAddress: string }) {
  const reaUrl = realestateListingUrl(fullAddress)
  const domainUrl = domainListingUrl(fullAddress)
  if (!reaUrl && !domainUrl) return null

  const linkClass =
    'inline-flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-border bg-white px-4 text-sm font-semibold text-charcoal transition-colors hover:bg-surface'

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted">
        View this property on
      </p>
      <div className="flex flex-col gap-2">
        {reaUrl ? (
          <a href={reaUrl} target="_blank" rel="noopener noreferrer" className={linkClass}>
            realestate.com.au
            <ExternalLink className="h-4 w-4 shrink-0 text-muted" />
          </a>
        ) : null}
        {domainUrl ? (
          <a href={domainUrl} target="_blank" rel="noopener noreferrer" className={linkClass}>
            Domain
            <ExternalLink className="h-4 w-4 shrink-0 text-muted" />
          </a>
        ) : null}
      </div>
    </div>
  )
}
